import { LitElement, html, css, nothing } from 'lit';
import { customElement, property, state, query } from 'lit/decorators.js';
import { choose } from 'lit/directives/choose.js';
import { classMap } from 'lit/directives/class-map.js';
import { connect } from 'pwa-helpers/connect-mixin.js';
import { store } from '../../store/store.js';
import { userManager } from '../../store/auth/userManager.js';
import { reloadCompositionState } from '../../store/composition/composition.js';
import { closeElearning } from '../../store/elearning/elearning.js';
import { launchTypes } from '../../store/elearning/helpers.js';
import '@sdb-develop/learning-os-player/sdb-os-player.js';
import '@sdb-develop/learning-frontend';
import '@polymer/paper-ripple';
import './sdb-url-player.js';
import '../scorm/sdb-scorm-player.js';

@customElement('sdb-elearning-dialog')
class SdbElearningDialog extends connect(store)(LitElement)
 {

    @property({ type: Boolean })
    accessor transparent;

    @state()
    accessor opened;

    @state()
    accessor backdrop = true;

    @state()
    accessor playerType;

    @state()
    accessor launchData;

    @state()
    accessor launchUrl;

    @state()
    accessor user;

    @query('#scorm_player')
    accessor scormPlayer;

    constructor() {
        super();

        window.addEventListener('message', msg => {
            switch (msg.data) {
                case 'close-elearning-modal':
                    store.dispatch(closeElearning());
                    store.dispatch(reloadCompositionState()); // failsafe call, signalR should have updated it by now
                    break;
                case 'close-elearning-and-notify':
                    store.dispatch(closeElearning({ command: 'notify' }));
                    store.dispatch(reloadCompositionState()); // failsafe call, signalR should have updated it by now
                    break;
            }
        });
    }

    stateChanged(state) {
        this.opened = state.elearning?.popupOpen === true;
        this.playerType = state.elearning.playerType;
        this.launchType = state.elearning.launchType;
        this.launchData = state.elearning.launchData;
        this.launchUrl = state.elearning?.popupOpen === true ? state.elearning.launchUrl : 'about:blank';
        this.autoClose = state.elearning?.popupOpen === true ? !!state.elearning.autoClose : true;
        this.user = state.auth.user;
        this.preview = state.env.preview;
    }

    static get styles() {
        return css`
            div#canvas:not([opened]) {
                display: none;
            }

            div#canvas[opened] {
                display: block;
                position: fixed;
                top: 0;
                left: 0;
                right: 0;
                bottom: 0;
                z-index: 1300;
                display: flex;
                align-items: center;
                justify-content: center;
            }

            div#canvas[opened][backdrop] {
                background: rgba(0, 0, 0, .3);
            }

            div#elearning_dialog {
                position: absolute;
                background: #fff;
                width: 90%;
                height: 90%;
                box-shadow: rgba(0, 0, 0, 0.16) 0px 10px 36px 0px, rgba(0, 0, 0, 0.06) 0px 0px 0px 1px;
                animation: zoomIn 0.3s;
            }

            div#elearning_dialog.transparent {
                background: transparent;
                box-shadow: none;
            }

            div#elearning_dialog.new-window {
                width: 40%;
                height: 30%;
            }

            sdb-url-player, sdb-os-player, sdb-scorm-player {
                width: 100%;
                height: 100%;
                border: none;
                margin:0;
                padding:0;
            }

            div.close {
                position: absolute;
                width: 40px;
                height: 40px;
                right: -30px;
                top: -30px;
                background: white;
                border: 1px solid #ededed;
                border-radius: 50%;
                padding: 0;
                margin: 0;
                display: flex;
                align-items: center;
                justify-content: center;
                cursor: pointer;
                box-shadow: 0 2px 2px 0 rgba(0, 0, 0, 0.14), 0 1px 5px 0 rgba(0, 0, 0, 0.12), 0 3px 1px -2px rgba(0, 0, 0, 0.2);
            }

            div.close[hidden] {
                display: none;
            }

            iron-icon {
                --iron-icon-width: 24px;
                --iron-icon-height: 24px;
            }

            @media only screen and (max-width: 768px) {
                div#elearning_dialog[autoclose] {
                    width: 100%;
                    min-width: 100%;
                    height: 100%;
                    min-height: 100%;
                }
            }

            @keyframes zoomIn {
                0% {
                    transform: scale(0);
                }
                100% {
                    transform: scale(1);
                }
            }
        `;
    }

    render() {
        if (!this.launchData && (!this.launchUrl || this.launchUrl === 'about:blank'))
            return nothing;

        return html`
            <div
                id="canvas"
                ?opened=${this.opened}
                ?backdrop=${this.backdrop}
                ?autoclose=${this.autoClose}>
                <div
                    id="elearning_dialog"
                    class=${classMap({
                        autoclose: this.autoClose,
                        transparent: this.transparent,
                        'new-window': this.launchType >= launchTypes.LAUNCHTYPE_NEWWINDOW,
                    })}>
                    ${choose(this.playerType, [
                            [0, this._renderUrlPlayer], // lti1
                            [1, this._renderOsPlayer], // os
                            [2, this._renderScormPlayer], // scorm
                            [3, this._renderUrlPlayer], // lti-advantage
                        ],
                        this._renderUrlPlayer,
                    )}
                    <div
                        class="close"
                        @click=${this._closeDialog}
                        ?hidden=${this.autoClose}>
                        <sdb-content-icon icon="fal fa-xmark"></sdb-content-icon>
                        <paper-ripple></paper-ripple>
                    </div>
                </div>
            </div>
        `;
    }

    _renderUrlPlayer = () => html`
        <sdb-url-player
            id="iframe_player"
            launchUrl=${this.launchUrl}
            launchType=${this.launchType}
            @close=${this._closeDialog}>
        </sdb-url-player>
    `;

    _renderOsPlayer = () => this.preview ?
        html`
            <sdb-os-player
                id="os_player"
                .user=${this.user}
                .settings=${userManager._settings}
                referenceId=${this.launchData?.referenceId}
                elearningId=${this.launchData?.elearningId}
                environment=${this.launchData?.environment}
                @playerExit=${this._closeDialog}
                browse>
            </sdb-os-player>
        ` : html`
            <sdb-os-player
                id="os_player"
                .user=${this.user}
                .settings=${userManager._settings}
                referenceId=${this.launchData?.referenceId}
                elearningId=${this.launchData?.elearningId}
                callback="composition"
                environment=${this.launchData?.environment}
                @playerExit=${this._closeDialog}>
            </sdb-os-player>
        `;


    _renderScormPlayer = () => html`
        <sdb-scorm-player
            id="scorm_player"
            packageId=${this.launchData?.packageId}
            sessionId=${this.launchData?.referenceId}
            launchType=${this.launchType}
            @finish=${this._closeDialog}>
        </sdb-scorm-player>
    `;

    _closeDialog = () => {
        if (!this.opened) {
            return;
        }

        store.dispatch(closeElearning({ command: 'notify' }));
        store.dispatch(reloadCompositionState());
    }
}
